import { useQuery } from '@apollo/client';
import { Meta, Spinner, theme } from 'aimo-ui';
import FlatListCustom from 'components/sheets/FlatListCustom';
import AccountContext from 'context/AccountContext';
import { READ_CITY_PMCS_BY_ADDRESS } from 'graphql/queries/readCityPMCsByAddress';
import useCityPmcsByMembergroup from 'hooks/useCityPmcsByMembergroup';
import useDebounce from 'hooks/useDebounce';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useCityParkingStore } from 'stores/cityParkingStore';
import { Divider } from 'styles/CommonLayoutStyles';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  LegallAddress,
  Maybe,
  Pmc,
  Query,
  QueryReadCityPmcsByAddressArgs,
} from 'types/generatedSchemaTypes';
import { splitAddress } from 'utils/commonUtils';
import { Container } from '../styles/styles';
import StreetParkingPmcsItem from './CityParkingSelectItem';
import NoPmcsFound from './NoPmcsFound';
import PurchaseOrAuthenticate from './PurchaseOrAuthenticate';
import { getCities } from 'utils/cityParkingUtils';

type StreetParkingSearchProps = {
  testID?: string;
};

const DividerMargin0: React.FC = () => <Divider marginHorizontal={0} />;

const RenderStreetParkingItem = (
  item: Maybe<Pmc>,
  canPurchase: boolean = true
) => <StreetParkingPmcsItem item={item} canPurchase={canPurchase} />;

const CityParkingListSearch: React.FC<StreetParkingSearchProps> = ({
  testID,
}) => {
  const searchTerm = useCityParkingStore((state) => state.searchTerm);
  const { t } = useTranslation();
  const {
    customer: { householdBenefits, strongAuthentication },
  } = useContext(AccountContext);

  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  const { streetName, streetNumber } = splitAddress(debouncedSearchTerm);
  const { city, countryCode } = useCityParkingStore(
    (state) => state.byMemberGroupSearchParams
  );

  const { data: searchedPmcData, loading: loadingByRead } = useQuery<
    Query,
    QueryReadCityPmcsByAddressArgs
  >(READ_CITY_PMCS_BY_ADDRESS, {
    variables: {
      input: {
        streetName,
        streetNumber,
        cities: getCities(city),
        countryCode,
      },
    },
    skip: !streetName || streetName.length < 3,
    fetchPolicy: 'no-cache',
  });

  const addressList: Maybe<LegallAddress>[] = React.useMemo(
    () => strongAuthentication?.addresses ?? [],
    [strongAuthentication]
  );

  const { cityPmcs, loading: loadingByMemberGroup } =
    useCityPmcsByMembergroup();
  const loading = loadingByMemberGroup || loadingByRead;
  const searchedPmcs = searchedPmcData?.readCityPmcsByAddress?.content ?? [];
  const purchaseablePmcs = cityPmcs.filter((p) =>
    householdBenefits?.some((benefit) => benefit.benefitTargetUid === p.pmcId)
  );

  const allPermits =
    searchedPmcs.length > 0
      ? searchedPmcs
      : cityPmcs.filter(
          (pmc) => !purchaseablePmcs.some((p) => p?.pmcId === pmc.pmcId)
        );

  if (loading) {
    return (
      <StyledWrapper mt={50} center>
        <Spinner />
      </StyledWrapper>
    );
  }

  if (streetName && streetNumber && searchedPmcs.length === 0) {
    return (
      <Container testID={testID}>
        <NoPmcsFound testID="city-pmcs-no-data" />
      </Container>
    );
  }

  if (
    searchTerm &&
    (!streetName || !streetNumber) &&
    searchedPmcs.length === 0
  ) {
    return (
      <Meta center testID="city-pmc-search-meta">
        {t('search.streetWithNumber')}
      </Meta>
    );
  }

  return (
    <Container testID={testID}>
      <PurchaseOrAuthenticate
        addressList={addressList as LegallAddress[]}
        purchaseablePmcs={purchaseablePmcs}
        renderItem={RenderStreetParkingItem}
      />
      <StyledWrapper>
        <Meta testID="city-pmcs-searc-meta" color={theme.colors.gray300}>
          {t('cityPermits.suggestionsList', { forAddress: null })}
        </Meta>
      </StyledWrapper>
      <FlatListCustom
        data={allPermits}
        ItemSeparatorComponent={DividerMargin0}
        renderItem={({ item }) => RenderStreetParkingItem(item, false)}
        keyExtractor={(item, index) => `${item?.id}-${index}`}
        testID="city-pmcs-search-list-applyable"
      />
    </Container>
  );
};

export default CityParkingListSearch;
